// 4 ago 2026 (pedido de Augusto): un emoji por categoría para identificar
// de un vistazo el tipo de ingreso/egreso en Transacciones y en el
// Calendario. Las categorías en la app vienen de 3 fuentes distintas que
// conviven en el mismo campo de texto (tx.categoria):
//   1. Los 7 slugs en inglés que devuelve la IA al importar estados de
//      cuenta (food/transport/bills/shopping/entertainment/salary/other,
//      ver EXTRACTION_SYSTEM_PROMPT en backend_new/.../statements.py).
//   2. Las 9 categorías fijas en español de esta página (Salario,
//      Alquiler, Alimentación, Transporte, Servicios, Entretenimiento,
//      Salud, Educación, Otro).
//   3. Categorías libres que el usuario escribe a mano (ej. "mascotas").
// Por eso el match es por palabra clave (normalizada, sin tildes) en vez
// de una tabla 1 a 1 — así "Alimentación", "food" y "Alimentacion" (sin
// tilde) caen todos en el mismo emoji sin tener que enumerar cada
// variante posible.
const REGLAS = [
  { emoji: '🍔', claves: ['food', 'alimenta', 'comida', 'restaurant', 'supermercado', 'grocer'] },
  { emoji: '🚗', claves: ['transport', 'movilidad', 'taxi', 'combustible', 'gasolina', 'pasaje'] },
  { emoji: '🧾', claves: ['bill', 'servicio', 'factura', 'luz', 'agua', 'internet', 'telefono', 'utilit'] },
  { emoji: '🛍️', claves: ['shopping', 'compra', 'ropa'] },
  { emoji: '🎬', claves: ['entertainment', 'entretenim', 'ocio', 'cine', 'streaming'] },
  { emoji: '💰', claves: ['salary', 'salario', 'sueldo', 'ingreso', 'honorario'] },
  { emoji: '🏠', claves: ['alquiler', 'renta', 'vivienda', 'hipoteca'] },
  { emoji: '⚕️', claves: ['salud', 'medic', 'farmacia', 'clinica', 'seguro'] },
  { emoji: '📚', claves: ['educac', 'colegio', 'universidad', 'curso'] },
  { emoji: '✈️', claves: ['viaje', 'travel', 'vacacion', 'hotel'] },
  { emoji: '🐾', claves: ['mascota', 'veterinar', 'pet'] },
  { emoji: '📦', claves: ['otro', 'other', 'otros'] },
];

// Categorías personalizadas que no calzan con ninguna regla anterior:
// en vez de un único ícono genérico para todas (que las haría
// indistinguibles entre sí, justo lo que se quiere evitar), se elige de
// forma determinística entre estas — el mismo texto de categoría siempre
// cae en el mismo emoji, pero categorías distintas casi siempre caen en
// emojis distintos.
const EMOJIS_FALLBACK = ['🏷️', '📌', '🔖', '🧩', '⭐', '🔷', '🎯', '💠'];

const normalizar = (texto) =>
  texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, ''); // quita tildes/diacríticos

// 28 ago 2026 (pedido de Augusto: "Categorias no te da la opcion de
// cambiar el emoji"): antes esto era 100% automático, sin forma de
// override -- ahora CategoriesPage.js deja elegir un emoji propio por
// categoría, que se guarda en el backend (Category.emoji, ver
// endpoints/categories.py). `overrides` es un mapa { nombreNormalizado:
// emoji } armado con buildEmojiOverrides (más abajo) a partir de las
// categorías personalizadas ya cargadas -- si la categoría pedida tiene
// un emoji propio guardado, se usa ese ANTES de aplicar la regla
// automática por palabra clave.
export const getCategoryEmoji = (categoria, overrides) => {
  if (!categoria || !categoria.trim()) return '📦';
  const norm = normalizar(categoria.trim());
  if (overrides && overrides[norm]) return overrides[norm];
  for (const regla of REGLAS) {
    if (regla.claves.some((clave) => norm.includes(clave))) return regla.emoji;
  }
  // Fallback determinístico por hash simple del texto normalizado.
  let hash = 0;
  for (let i = 0; i < norm.length; i += 1) {
    hash = (hash * 31 + norm.charCodeAt(i)) >>> 0;
  }
  return EMOJIS_FALLBACK[hash % EMOJIS_FALLBACK.length];
};

// Arma el mapa de overrides que espera getCategoryEmoji, a partir de una
// lista de categorías del backend (customCategories, ver
// categoryService.getAll()) -- cada una con { nombre, emoji }. Categorías
// sin emoji propio (emoji null/undefined) simplemente no entran al mapa,
// así getCategoryEmoji sigue cayendo en la regla automática para ellas.
export const buildEmojiOverrides = (categorias) => {
  const mapa = {};
  (categorias || []).forEach((c) => {
    if (c && c.emoji && c.nombre) {
      mapa[normalizar(c.nombre.trim())] = c.emoji;
    }
  });
  return mapa;
};

export default getCategoryEmoji;
